import { defineStore } from 'pinia'
import Network from "~/utils/network";
import { useSessionStore } from './session';

const CUSTOMER_STATUS_STORAGE_KEY = "fintrous_instacash_status_storage";

const urls = {
    statementAcceptances: `/statements`,
    profile: `/profile`,
    profileUpdate: `/profile/update`,
    customerUpdate: `/customer/update`,
    statusUpdate: "/profile/status",
    passwordChange: `/bulwark/customer/password-change`,
    isEmailConfirmed: "/bulwark/customer/is-email-confirmed",
    sendConfirmationEmail: "/bulwark/customer/auth/send-confirmation-email",
    customerEmailConfirm: "/bulwark/customer/auth/confirm-email",
    businessEmailConfirm: "/bulwark/customer/auth/confirm-business-email",
    changeEmail: "/bulwark/customer/auth/change-email",
    addBusinessEmail: "/bulwark/customer/auth/add-business-email",
    changePhone: "/bulwark/customer/auth/change-phone",
    confirmPhone: "/bulwark/customer/auth/confirm-phone",
    getBusinessEmail: (publicId) => `/bulwark/customer/auth/business-email/${publicId}`,
    getBusinessEmailByCompanyId: (companyId) => `/bulwark/customer/auth/business-email-company/${companyId}`,
    getApplicationCount: `/bnpl/customer/application/my-applications`,
};

export const useProfileStore = defineStore('profile', {
    state: () => {
        return {
            profile: {},
        };
    },
    actions: {
        fetch: async function () {
            try {
                const sessionStore = useSessionStore();
                if (!sessionStore.isLoggedIn) {
                    const errorMessage = "Requesting profile data while logged out."
                    console.error(errorMessage);
                }
                const data = await Network.http(urls.profile);
                this.saveProfile(data);
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        fetchWithPublicId: async function (publicId) {
            try {
                const sessionStore = useSessionStore();
                if (!sessionStore.isLoggedIn) {
                    const errorMessage = "Requesting profile data while logged out."
                    console.error(errorMessage);
                }
                const data = await Network.http(urls.profile + `/${publicId}`);
                this.saveProfile(data);
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        save: async function (request) {
            try {
                await Network.http(urls.profileUpdate,
                    { body: request, method: 'PUT'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        customerUpdate: async function (request) {
            try {
                await Network.http(urls.customerUpdate, { body: request, method: 'PUT'} );
                this.profile = { ...this.profile, ...request};
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        updateLocalZip: function (zip) {
            this.profile.postCode = zip;
        },
        passwordChange: async function (request) {
            try {
                await Network.http(urls.passwordChange,
                    { body: request, method: 'POST' });
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        emailChange: async function (request) {
            try {
                return await Network.http(urls.changeEmail,
                    { body: request, method: 'POST'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        phoneChange: async function (request) {
            try {
                const response = await Network.http(urls.changePhone,
                    { body: request, method: 'POST'} );
                return response.tokenId;
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        confirmPhone: async function (request) {
            try {
                await Network.http(urls.confirmPhone,
                    { body: request, method: 'POST'} );
            } catch (e) {
                console.log(e);
                return Promise.reject(e);
            }
        },
        sendConfirmationEmail: async function (request) {
            try {
                return await Network.http(urls.sendConfirmationEmail,
                    { body: request, method: 'POST'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },

        updateStatus: async function ({ status, result }) {
            try {
                await Network.http(urls.statusUpdate,
                    {
                        body: {currentStatus: status, currentStatusResult: result,},
                        method: 'PUT',
                    }
            );
                this.saveStatus(status);
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        isEmailConfirmed: async function () {
            try {
                let response = await Network.http.raw(urls.isEmailConfirmed);
                return response.data;
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        confirmEmail: async function (request) {
            try {
                await Network.http(urls.customerEmailConfirm,
                    { body: request, method: 'POST'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        saveProfile: function (data) {
            this.profile = data;
        },
        getBusinessEmail: async function (publicId) {
            try {
                const response = await Network.http(urls.getBusinessEmail(publicId));
                return response;
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        getBusinessEmailByCompanyId: async function (companyId) {
            try {
                const response = await Network.http(urls.getBusinessEmailByCompanyId(companyId));
                return response;
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        addBusinessEmail: async function (request) {
            try {
                return await Network.http(urls.addBusinessEmail, { body: request, method: 'POST'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        confirmBusinessEmail: async function (request) {
            try {
                await Network.http(urls.businessEmailConfirm, { body: request, method: 'POST'} );
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        },
        async fetchApplicationCount() {
            try {
                return await Network.http(urls.getApplicationCount);
            } catch (err) {
                console.log(err);
                await Promise.reject(err);
            }
        }
    },

    getters: {
      firstName() {
        return this.profile.firstName;
      },
      lastName() {
        return this.profile.lastName;
      },
      mothersName() {
        return this.profile.mothersName;
      },
      placeOfBirth() {
        return this.profile.placeOfBirth;
      },
      dateOfBirth() {
        return this.profile.dateOfBirth;
      },
      postCode() {
        return this.profile.postCode;
      },
      city() {
        return this.profile.city;
      },
      street() {
        return this.profile.street;
      },
      houseNumber() {
        return this.profile.houseNumber;
      },
      isRisky() {
        return this.profile.isRisky;
      },
      isTrusted() {
        return this.profile.isTrusted;
      }
    },
})